import React, { useEffect, useState } from 'react';
import {
  Shield,
  Lock,
  Key,
  Eye,
  Fingerprint,
  Server,
  AlertTriangle,
  CheckCircle,
  ShieldCheck,
  BadgeCheck,
  Globe,
  Zap,
  ArrowRight,
  Database,
  Network,
  Scan,
  UserCheck,
  KeyRound,
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const securityHighlights = [
  {
    title: 'Data Encryption',
    value: '256-bit AES',
    icon: Lock,
    description: 'End-to-end encryption for all data at rest and in transit with TLS 1.3 protocols.',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    title: 'Uptime SLA',
    value: '99.99%',
    icon: ShieldCheck,
    description: 'Enterprise-grade infrastructure with redundant data centers and automated failover.',
    color: 'from-green-500 to-emerald-500',
  },
  {
    title: 'Certifications',
    value: 'ISO 27001',
    icon: BadgeCheck,
    description: 'Certified for information security management and GDPR, DPDP Act 2023 compliant.',
    color: 'from-purple-500 to-pink-500',
  },
];

const securityFeatures = [
  {
    icon: Lock,
    title: 'End-to-End Encryption',
    description:
      'Military-grade AES-256 encryption protects your fleet data at every stage. Data is encrypted in transit using TLS 1.3 and at rest with industry-standard encryption algorithms.',
    image:
      'https://plus.unsplash.com/premium_photo-1681487843757-8a6d04422652?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8RW5kLXRvLUVuZCUyMEVuY3J5cHRpb258ZW58MHx8MHx8fDA%3D',
  },
  {
    icon: UserCheck,
    title: 'Multi-Level Access Control',
    description:
      'Granular role-based permissions ensure only authorized personnel can access sensitive information. Supports SSO, MFA, and custom access policies for enterprise needs.',
    image:
      'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&auto=format&fit=crop&q=60',
  },
  {
    icon: ShieldCheck,
    title: 'Zero-Trust Architecture',
    description:
      'Never trust, always verify. Our zero-trust security model validates every request and enforces strict authentication and authorization at every layer of the system.',
    image:
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&auto=format&fit=crop&q=60',
  },
];

const securityPillars = [
  {
    title: 'Identity & Access Management',
    description:
      'Comprehensive IAM solution with single sign-on (SSO), multi-factor authentication (MFA), and role-based access control (RBAC) to secure user identities and permissions.',
    points: [
      'SAML 2.0 and OAuth 2.0 integration for enterprise SSO',
      'Biometric authentication support for mobile devices',
      'Just-in-time access provisioning and deprovisioning',
      'Session management with configurable timeout policies',
    ],
    icon: Fingerprint,
  },
  {
    title: 'Data Protection & Privacy',
    description:
      'Protect sensitive fleet data with encryption, data loss prevention (DLP), and privacy controls that meet global compliance requirements including GDPR and DPDP Act 2023.',
    points: [
      'Automatic data classification and encryption policies',
      'Field-level encryption for PII and sensitive attributes',
      'Right to erasure and data portability workflows',
      'Privacy-preserving analytics with differential privacy',
    ],
    icon: Database,
  },
  {
    title: 'Threat Detection & Response',
    description:
      'Advanced security monitoring with AI-powered threat detection, real-time alerts, and automated incident response to protect against cyber attacks and data breaches.',
    points: [
      '24/7 security operations center (SOC) monitoring',
      'Behavioral analytics to detect anomalous activities',
      'Automated incident response and threat containment',
      'Security event correlation and threat intelligence feeds',
    ],
    icon: AlertTriangle,
  },
];

const complianceStandards = [
  { name: 'ISO 27001', description: 'Information Security Management' },
  { name: 'SOC 2 Type II', description: 'Security, Availability & Confidentiality' },
  { name: 'GDPR', description: 'European Data Protection Regulation' },
  { name: 'DPDP Act 2023', description: 'India Digital Personal Data Protection' },
  { name: 'AIS-140', description: 'Indian Vehicle Tracking Standards' },
];

const SecurityPage = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <div
      className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
        theme === 'dark'
          ? 'bg-gradient-to-r from-[#0b1120] via-[#1a1f37] to-[#2d1b4e] text-slate-100'
          : 'bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 text-slate-900'
      }`}
    >
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Security" />

      <div className="absolute inset-0 pointer-events-none">
        {theme === 'dark' ? (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/3 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-[160px] animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] bg-blue-500/20 rounded-full blur-[200px] animate-pulse delay-500" />
          </>
        ) : (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/3 w-[30rem] h-[30rem] bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
          </>
        )}
      </div>

      <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          <header className="space-y-5 text-center">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${
                theme === 'dark'
                  ? 'bg-slate-900 border-1 border-muted-foreground/20 text-green-200'
                  : 'bg-white border-1 border-muted-foreground/20 text-green-700'
              }`}
            >
              <Shield className="h-4 w-4" />
              Enterprise Security
            </div>
            <h1 className="text-sm sm:text-4xl md:text-5xl font-bold leading-tight">
              Military-Grade Security for Your Fleet Data
            </h1>
            <p
              className={`text-sm sm:text-lg md:text-xl max-w-3xl mx-auto ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              TrackYourWheelz employs defense-in-depth security architecture with encryption, access
              controls, and compliance certifications to protect your mission-critical fleet operations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://trackyourwheelz.com/security', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                View Security Report
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('mailto:security@trackyourwheelz.com', '_blank')}
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                Contact Security Team
              </Button>
            </div>
          </header>

          <section className="grid gap-6 md:grid-cols-3">
            {securityHighlights.map((highlight) => {
              const Icon = highlight.icon;
              return (
                <Card
                  key={highlight.title}
                  className={`border-1 border-muted-foreground/20 ${
                    theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                  } shadow-lg`}
                >
                  <CardHeader className="space-y-3">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${highlight.color} flex items-center justify-center`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-sm sm:text-base font-semibold">
                      {highlight.title}
                    </CardTitle>
                    <CardDescription
                      className={`text-2xl sm:text-3xl font-bold ${
                        theme === 'dark' ? 'text-green-200' : 'text-green-600'
                      }`}
                    >
                      {highlight.value}
                    </CardDescription>
                  </CardHeader>
                  <CardContent
                    className={`text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                    }`}
                  >
                    {highlight.description}
                  </CardContent>
                </Card>
              );
            })}
          </section>

          <section className="space-y-6">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Security Architecture</h2>
            <p
              className={`text-sm sm:text-base max-w-3xl mx-auto text-center ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              Our defense-in-depth approach layers multiple security controls to protect your data from
              threats at every level of the stack.
            </p>
            <div className="grid gap-6 lg:grid-cols-3">
              {securityFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className={`overflow-hidden border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <div className="relative h-40">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = '/icons/enterprise-security.jpg';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-semibold">{feature.title}</span>
                      </div>
                    </div>
                    <CardContent
                      className={`p-5 text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                      }`}
                    >
                      {feature.description}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="space-y-8">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Security Pillars</h2>
            <div className="space-y-6">
              {securityPillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <Card
                    key={pillar.title}
                    className={`border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                      <div
                        className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                          theme === 'dark'
                            ? 'bg-green-500/20 text-green-200'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-2">
                        <CardTitle className="text-sm sm:text-lg font-semibold">
                          {pillar.title}
                        </CardTitle>
                        <CardDescription
                          className={`text-sm ${
                            theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                          }`}
                        >
                          {pillar.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <ul
                        className={`grid gap-3 sm:grid-cols-2 text-sm ${
                          theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {pillar.points.map((point) => (
                          <li key={point} className="flex items-start gap-2">
                            <CheckCircle className="mt-1 h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <Network className="h-5 w-5 text-blue-500" />
                  Infrastructure Security
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Our cloud infrastructure is built on secure, compliant platforms with redundant data
                  centers, automated backups, and disaster recovery capabilities.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  TrackYourWheelz leverages AWS and Azure infrastructure with SOC 2 Type II compliance,
                  regular penetration testing, and continuous security monitoring.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Server className="mt-1 h-4 w-4 text-blue-500" />
                    Multi-region data replication with 99.99% uptime SLA
                  </li>
                  <li className="flex items-start gap-2">
                    <Scan className="mt-1 h-4 w-4 text-blue-500" />
                    Automated vulnerability scanning and patch management
                  </li>
                  <li className="flex items-start gap-2">
                    <Eye className="mt-1 h-4 w-4 text-blue-500" />
                    Real-time security event monitoring and alerting
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-blue-500" />
                  Compliance & Certifications
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  TrackYourWheelz meets international security and privacy standards required for
                  enterprise deployments across industries.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  We maintain certifications and comply with regulations to ensure your fleet data meets
                  the highest security and privacy standards.
                </p>
                <div className="grid gap-2">
                  {complianceStandards.map((standard) => (
                    <div
                      key={standard.name}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50'
                      }`}
                    >
                      <span className="font-semibold text-sm">{standard.name}</span>
                      <span
                        className={`text-xs ${
                          theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
                        }`}
                      >
                        {standard.description}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="rounded-3xl overflow-hidden relative">
            <img
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1600&auto=format&fit=crop&q=60"
              alt="Security infrastructure"
              className="h-72 sm:h-80 w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.target.src = '/icons/enterprise-security.jpg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-y-0 left-0 px-6 sm:px-10 py-8 sm:py-10 flex flex-col justify-between text-white max-w-xl">
              <div className="space-y-3">
                <h2 className="text-sm sm:text-3xl font-bold">Security-first by design</h2>
                <p className="text-sm sm:text-base opacity-80">
                  Every feature, API, and integration is built with security as the foundation. We
                  conduct regular security audits, penetration testing, and threat modeling to stay ahead
                  of evolving threats.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
                <span className="rounded-full border border-white/40 px-4 py-1">Bug bounty program</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Security audits</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Penetration testing</span>
              </div>
            </div>
          </section>

          <footer
            className={`rounded-2xl border-1 border-muted-foreground/20 p-6 sm:p-8 text-center ${
              theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
            }`}
          >
            <h2 className="text-sm sm:text-3xl font-bold mb-4">Need security documentation?</h2>
            <p
              className={`text-sm sm:text-base max-w-2xl mx-auto mb-6 ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              Download our security whitepaper, compliance reports, and architecture documentation to
              share with your security and compliance teams.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://trackyourwheelz.com/security/whitepaper', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                Download Whitepaper
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('mailto:security@trackyourwheelz.com', '_blank')}
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                Request Security Assessment
              </Button>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default SecurityPage;
